/**
 * SampleDetailDialog — popup compartido entre Almacén (/warehouse) y la pestaña
 * Muestras dentro de cada estudio. Renderiza:
 *  - Datos basicos de la muestra (producto, lote, stock, peso/uds, almacenamiento,
 *    ubicacion, caducidad, codigo ciego).
 *  - Historial cronologico: movimientos y dispensaciones.
 *
 * Props:
 *  - sample (object|null): documento de la muestra. null cierra el dialogo.
 *  - movements, dispensations (array): se cargan desde
 *    GET /api/samples/{id}/movements y /dispensations (el padre los pasa).
 *  - studyCode (string|undefined): si se proporciona, se muestra una fila "Estudio".
 *    En el contexto de Almacen viene de studyMap[sample.study_id]?.code.
 *    En el contexto del estudio se omite (ya estamos dentro de el).
 *  - lang ('es'|'en')
 *  - onClose: callback al cerrar.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { storageLabelsStudy } from './study/studyConstants';

const LABELS = {
  es: {
    detail: 'Detalle de muestra', product: 'Producto', lot: 'Lote', study: 'Estudio',
    stock: 'Stock', unit_weight: 'Peso por unidad (g)', initial_qty: 'Cantidad inicial (uds)',
    measurement: 'Medicion', weight_g: 'Peso (g)', units: 'Unidades',
    storage: 'Almacenamiento', location: 'Ubicacion', expiry: 'Caducidad',
    blind_code: 'Codigo ciego', history: 'Historial', dispense: 'Dispensar',
    no_history: 'Sin movimientos registrados',
    muestroteca_label: 'Dispensado a muestroteca',
    muestroteca: 'MUESTROTECA',
    yes: 'SI', no: 'NO',
  },
  en: {
    detail: 'Sample detail', product: 'Product', lot: 'Lot', study: 'Study',
    stock: 'Stock', unit_weight: 'Unit weight (g)', initial_qty: 'Initial qty (units)',
    measurement: 'Measurement', weight_g: 'Weight (g)', units: 'Units',
    storage: 'Storage', location: 'Location', expiry: 'Expiry',
    blind_code: 'Blind code', history: 'History', dispense: 'Dispense',
    no_history: 'No movements recorded',
    muestroteca_label: 'Dispensed to muestroteca',
    muestroteca: 'MUESTROTECA',
    yes: 'YES', no: 'NO',
  },
};

export default function SampleDetailDialog({ sample, movements = [], dispensations = [], studyCode, lang = 'es', onClose }) {
  const lb = LABELS[lang] || LABELS.es;
  const isUnits = (sample?.measure_mode || 'peso') === 'unidades';

  return (
    <Dialog open={!!sample} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sample-detail-dialog">
        <DialogHeader>
          <DialogTitle>{lb.detail}: {sample?.sample_id}</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        {sample && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-stone-500">{lb.product}:</span> <span className="font-medium">{sample.product_name}</span></div>
              <div><span className="text-stone-500">{lb.lot}:</span> <span className="font-mono">{sample.manufacturer_lot}</span></div>
              {studyCode && (
                <div><span className="text-stone-500">{lb.study}:</span> {studyCode}</div>
              )}
              <div><span className="text-stone-500">{lb.stock}:</span> <span className="font-semibold">{sample.stock_remaining}/{sample.quantity_received}</span></div>
              <div>
                <span className="text-stone-500">{isUnits ? `${lb.initial_qty}:` : `${lb.unit_weight}:`}</span>{' '}
                <span className="font-semibold">{sample.unit_weight || '-'}{isUnits ? ' uds' : ' g'}</span>
              </div>
              <div>
                <span className="text-stone-500">{lb.measurement}:</span>{' '}
                <span className="font-medium">{isUnits ? lb.units : lb.weight_g}</span>
              </div>
              <div><span className="text-stone-500">{lb.storage}:</span> {storageLabelsStudy[lang]?.[sample.storage_condition] || sample.storage_condition}</div>
              <div><span className="text-stone-500">{lb.location}:</span> {sample.location || '-'}</div>
              <div><span className="text-stone-500">{lb.expiry}:</span> {sample.expiry_date || '-'}</div>
              {sample.is_blind && (
                <div><span className="text-stone-500">{lb.blind_code}:</span> <span className="font-mono">{sample.blind_code}</span></div>
              )}
            </div>
            <div>
              <h3 className="text-xs font-semibold text-stone-500 uppercase mb-2">{lb.history}</h3>
              <div className="space-y-1.5">
                {movements.length === 0 && dispensations.length === 0 && (
                  <p className="text-xs text-stone-400 text-center py-2">{lb.no_history}</p>
                )}
                {movements.map(m => (
                  <div key={m.movement_id} className="flex items-center gap-3 text-xs bg-stone-50 border border-stone-200 rounded-sm px-3 py-1.5">
                    <Badge variant="outline" className="text-[10px]">{m.type}</Badge>
                    <span className="text-stone-600">{m.date}</span>
                    <span className="font-mono">{m.quantity > 0 ? `x${m.quantity}` : ''}</span>
                    <span className="text-stone-500 flex-1 truncate">{m.notes}</span>
                    <span className="text-stone-400">{m.user_email}</span>
                  </div>
                ))}
                {dispensations.map(d => (
                  <div key={d.dispensation_id} className={`flex items-center gap-3 text-xs border rounded-sm px-3 py-1.5 ${d.to_muestroteca ? 'bg-purple-50 border-purple-200' : 'bg-green-50 border-green-200'}`}>
                    <Badge variant="outline" className={`text-[10px] ${d.to_muestroteca ? 'bg-purple-100 text-purple-800 border-purple-300' : 'bg-green-100 text-green-700'}`}>{lb.dispense}</Badge>
                    <span className="text-stone-600">{d.date}</span>
                    {d.to_muestroteca ? (
                      <Badge variant="outline" className="text-[10px] font-mono font-semibold bg-purple-100 text-purple-800 border-purple-300" data-testid={`disp-muestroteca-badge-${d.dispensation_id}`}>{lb.muestroteca}</Badge>
                    ) : (
                      <>
                        <span className="font-mono font-semibold">{d.study_volunteer_code}</span>
                        <span className="text-stone-500">{d.visit_code}</span>
                      </>
                    )}
                    <span className="font-mono">x{d.quantity}</span>
                    <span className="text-stone-400 ml-auto">{d.dispensed_by}</span>
                  </div>
                ))}
              </div>
              {/* URS-F349: fila resumen — muestroteca SI / NO */}
              {(movements.length > 0 || dispensations.length > 0) && (() => {
                const hasMuestroteca = dispensations.some(d => d.to_muestroteca);
                return (
                  <div className="flex items-center justify-between gap-3 mt-3 px-3 py-2 bg-stone-50 border border-stone-200 rounded-sm" data-testid="sample-muestroteca-row">
                    <span className="text-xs font-semibold text-stone-700">{lb.muestroteca_label}:</span>
                    {hasMuestroteca
                      ? <Badge className="bg-purple-100 text-purple-800 border-purple-300 text-xs">{lb.yes}</Badge>
                      : <Badge className="bg-stone-100 text-stone-600 border-stone-300 text-xs">{lb.no}</Badge>}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
